import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';

@customElement('as-segmented-input')
export class SegmentedInput extends LitElement {
  private static readonly DIGIT_REGEX_NUM = /^[0-9]+$/g;
  private static readonly DIGIT_REGEX_ALPHANUM = /^[a-zA-Z0-9]+$/g;
  private static readonly INPUT_EVENT_TYPE = 'input';
  private static readonly COMPLETE_EVENT_TYPE = 'complete';
  private static readonly BACKSPACE_KEY = 'Backspace';
  private static readonly ENTER_KEY = 'Enter';

  public static styles = css`
    .segmented-input {
      display: flex;
      flex-direction: row;
      justify-content: center;
      gap: 0.3em;
    }

    .digit {
      font-size: 1.2em;
      width: 1.8em;
      padding: 0.3em 0em;
      text-align: center;
      border: 1px solid #cccccc;
    }
  `;

  @property({ type: Number })
  public digits = 6;

  @property({ type: Boolean })
  public letters = false;

  @property({ type: Boolean })
  public submitOnComplete = false;

  @state()
  private values: string[] = [];

  @state()
  private lastValue = '';

  private inputRefs: Ref<HTMLInputElement>[] = [];
  private internals: ElementInternals;

  public static get formAssociated() {
    return true;
  }

  public constructor() {
    super();
    this.internals = this.attachInternals();
    this.internals.setFormValue('');
  }

  public render() {
    this.inputRefs = [];

    return html`<div class="segmented-input">
      ${Array.from(Array(this.digits).keys()).map((i) => {
        const inputRef = createRef<HTMLInputElement>();
        this.inputRefs.push(inputRef);

        return html`<input
          ${ref(inputRef)}
          type="text"
          inputmode=${this.letters ? 'text' : 'numeric'}
          aria-label=${'Symbol ' + i}
          class="digit"
          autofocus
          autocomplete="off"
          @focus=${this.handleFocus}
          @input=${this.handleInput(i)}
          @keydown=${this.handleKeyDown}
        />`;
      })}
    </div>`;
  }

  public formResetCallback() {
    for (const inputRef of this.inputRefs) {
      if (inputRef.value) {
        inputRef.value.value = '';
      }
    }
  }

  public focusFirstInput() {
    this.inputRefs[0].value?.focus();
  }

  private handleFocus(event: InputEvent) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    input.select();
  }

  private handleInput(index: number) {
    return (event: InputEvent) => {
      const currentInput = event.target;

      if (!(currentInput instanceof HTMLInputElement)) {
        return;
      }

      const nextInput = currentInput.nextElementSibling;
      const inputValue = currentInput.value;

      if (this.letters) {
        if (!inputValue.match(SegmentedInput.DIGIT_REGEX_ALPHANUM)) {
          currentInput.value = '';
          return;
        }
      } else {
        if (!inputValue.match(SegmentedInput.DIGIT_REGEX_NUM)) {
          currentInput.value = '';
          return;
        }
      }

      if (inputValue.length === 0) {
        return;
      }

      const originalInput = currentInput.value;
      currentInput.value = currentInput.value.toUpperCase().substring(0, 1);

      this.values[index] = currentInput.value;
      this.values = [...this.values];

      if (nextInput instanceof HTMLInputElement) {
        if (inputValue.length === 1) {
          nextInput.focus();
          nextInput.select();
        }
      } else {
        this.handleComplete();
      }

      if (originalInput.length > 1) {
        this.fillOutNextInput(originalInput, nextInput);
      }
    };
  }

  private fillOutNextInput(originalInput: string, nextInput: Element | null) {
    if (nextInput instanceof HTMLInputElement) {
      nextInput.value = originalInput.substring(1);
      nextInput.focus();
      nextInput.dispatchEvent(new Event(SegmentedInput.INPUT_EVENT_TYPE));
    }
  }

  private handleComplete() {
    const currentValue = this.values.join('').substring(0, this.digits);
    this.internals.setFormValue(currentValue);

    if (this.lastValue === currentValue) {
      return;
    }

    if (this.submitOnComplete) {
      this.internals.form?.requestSubmit();
    } else {
      setTimeout(() => {
        this.dispatchEvent(
          new CustomEvent(SegmentedInput.COMPLETE_EVENT_TYPE, {
            detail: currentValue,
            bubbles: true,
            composed: true
          })
        );
      }, 0);
    }

    this.lastValue = currentValue;
  }

  private handleKeyDown(event: Event) {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }

    if (event.key === SegmentedInput.BACKSPACE_KEY) {
      this.handleBackspace(event);
    } else if (event.key === SegmentedInput.ENTER_KEY) {
      this.handleEnter();
    }
  }

  private handleBackspace(event: KeyboardEvent) {
    const currentInput = event.target;

    if (!(currentInput instanceof HTMLInputElement)) {
      return;
    }

    const previousInput = currentInput.previousElementSibling;
    const inputValue = currentInput.value;

    if (previousInput instanceof HTMLInputElement && inputValue.length === 0) {
      previousInput.value = '';
      previousInput.focus();
    }
  }

  private handleEnter() {
    const closestForm = this.closest('form');
    closestForm?.requestSubmit();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'segmented-input': SegmentedInput;
  }
}
